import {
  useGetRecordingQuery,
  useInfiniteScroll,
  useLazyGetWebObjectQuery,
} from '@carboncopyco/data';
import type { IRecordingPage } from '@carboncopyco/types';
import {
  Grid,
  SkeletonImageLoading,
  TextBase,
  TextLargeParagraph,
  TooltipBubble,
} from '@carboncopyco/ui';
import { AspectRatio } from '@radix-ui/react-aspect-ratio';
import { useEffect } from 'react';
import { InfiniteList } from '../../../InfiniteList';
import { Screenshot } from '../../../Screenshot';
import { TitleCard } from '../../InfoPageCards/TitleCard';
import { useClonePageProps } from '../ClonePageContext';

export const ScreensCard = () => {
  const { organizationId, cloneId } = useClonePageProps();
  const { data: clone } = useGetRecordingQuery({
    organizationId,
    id: cloneId,
  });
  const {
    items: screens,
    isLoading: isLoadingScreens,
    error: errorScreens,
    isLoadingBatch,
    fetchFirstBatch,
    fetchNextBatch,
  } = useInfiniteScroll(useLazyGetWebObjectQuery);

  const fetchScreensArgs = {
    organizationId,
    sourceType: 'record',
    sourceId: cloneId,
    revisionId: String(clone?.revisionId),
  };

  useEffect(() => {
    if (clone) {
      fetchFirstBatch(fetchScreensArgs);
    }
  }, [clone]);

  return (
    <TitleCard
      header={
        <div className="flex items-center gap-2 p-6">
          <TextLargeParagraph
            color="text-ds-natural-700"
            label="Screens"
          />
          {screens && <TextLargeParagraph label={`(${screens.length})`} />}
        </div>
      }
      radius
    >
      <div className="h-[290px]">
        <div className="h-full p-6 overflow-hidden overflow-x-hidden">
          <InfiniteList
            onReachBottom={() => fetchNextBatch(fetchScreensArgs)}
            error={
              errorScreens && {
                message: 'Error fetching screens.',
              }
            }
            isLoading={isLoadingScreens}
            loader={
              <SkeletonImageLoading
                cols={4}
                rows={2}
              />
            }
            isLoadingBatch={isLoadingBatch}
          >
            <Grid<IRecordingPage>
              gap="xl"
              keyName="url"
              data={screens}
              renderItem={({ image, title }) => (
                <div className="pr-1">
                  <div>
                    <AspectRatio ratio={16 / 10}>
                      <Screenshot image={image} />
                    </AspectRatio>
                    <TooltipBubble
                      title={title}
                      placement="top"
                      disabled={title?.length < 29}
                    >
                      <div className="w-56 truncate text-ds-natural-400">
                        <TextBase
                          label={title || 'Missing title'}
                          color="text-ds-natural-400"
                          ellipsis
                        />
                      </div>
                    </TooltipBubble>
                  </div>
                </div>
              )}
            />
          </InfiniteList>
        </div>
      </div>
    </TitleCard>
  );
};
